import { LitElement, html, css } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { sharedStyles } from '../styles/shared.js'
import { discoverWallets, pickWallet, type WalletAnnouncement } from '@attestto/id-wallet-adapter'
import {
  hashFile,
  signWithWallet,
  signWithBrowserKey,
  getBrowserKeyPair,
  exportCredentialAsJson,
  type DocumentSignatureCredential,
} from '../composables/document-signer.js'
import { loadPdfJs, formatPdfDate } from '../composables/pdf-verifier.js'
import { injectAttestationPage } from '../composables/pdf-attestation.js'
import { signPdfSelfAttested, buildExtensionSigner } from '../composables/attestto-self-sign.js'
import { t, currentLang, type Lang } from '../i18n.js'

interface PdfMeta {
  title: string | null
  author: string | null
  subject: string | null
  creator: string | null
  producer: string | null
  creationDate: string | null
  modDate: string | null
  pages: number | null
}

/**
 * <attestto-sign> — Sign a PDF with any DID wallet or browser key
 *
 * UI-only component. All crypto and VC construction lives in
 * composables/document-signer.ts — v2 changes touch one file.
 *
 * Flow:
 *   1. discoverWallets() finds installed credential wallets
 *   2. User picks a wallet (or auto-selects, or falls back to browser key)
 *   3. User drops a PDF
 *   4. signWithWallet() or signWithBrowserKey() produces a W3C VC
 *   5. VC pushed to wallet (or exported as JSON)
 */
@customElement('attestto-sign')
export class AttesttoSign extends LitElement {
  static override styles = [
    sharedStyles,
    css`
      :host {
        display: block;
        font-family: var(--attestto-font, system-ui, -apple-system, sans-serif);
        color: var(--attestto-text, #1a1a2e);
      }

      .drop-zone {
        border: 2px dashed var(--attestto-border, #cbd5e1);
        border-radius: 12px;
        padding: 3rem 2rem;
        text-align: center;
        cursor: pointer;
        transition: all 0.2s ease;
        background: var(--attestto-bg, #f8fafc);
      }

      .drop-zone:hover,
      .drop-zone.dragging {
        border-color: var(--attestto-primary, #594fd3);
        background: var(--attestto-bg-hover, #eef2ff);
      }

      .drop-zone-icon {
        font-size: 2.5rem;
        margin-bottom: 0.75rem;
      }

      input[type='file'] {
        display: none;
      }

      /* ── Wallet Status ──────────────────────────────────── */
      .wallet-status {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.75rem 1rem;
        border-radius: 8px;
        font-size: 0.85rem;
        margin-bottom: 1rem;
      }

      .wallet-connected {
        background: var(--attestto-success-bg, #dcfce7);
        color: var(--attestto-success, #16a34a);
      }

      .wallet-discovering {
        background: var(--attestto-info-bg, #dbeafe);
        color: var(--attestto-info, #2563eb);
      }

      .wallet-missing {
        background: var(--attestto-warning-bg, #fef3c7);
        color: var(--attestto-warning, #d97706);
      }

      .wallet-missing a {
        color: inherit;
        text-decoration: underline;
      }

      /* ── Wallet Card (connected state) ─────────────────── */
      .wallet-card {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.75rem 1rem;
        border: 1px solid var(--attestto-border, #e2e8f0);
        border-radius: 8px;
        background: var(--attestto-bg-card, #ffffff);
        margin-bottom: 1rem;
      }

      .wallet-card-icon {
        width: 32px;
        height: 32px;
        border-radius: 6px;
        object-fit: contain;
      }

      .wallet-card-info {
        flex: 1;
        min-width: 0;
      }

      .wallet-card-name {
        font-size: 0.85rem;
        font-weight: 600;
      }

      .wallet-card-meta {
        font-size: 0.72rem;
        color: var(--attestto-text-muted, #64748b);
      }

      .wallet-card-disconnect {
        background: none;
        border: none;
        color: var(--attestto-text-muted, #94a3b8);
        cursor: pointer;
        font-size: 0.75rem;
        text-decoration: underline;
      }

      /* ── Sign Card ─────────────────────────────────────── */
      .sign-card {
        border: 1px solid var(--attestto-border, #e2e8f0);
        border-radius: 12px;
        padding: 1.5rem;
        background: var(--attestto-bg-card, #ffffff);
      }

      .file-info {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        margin-bottom: 1.25rem;
        font-weight: 600;
      }

      .sign-btn {
        width: 100%;
        padding: 0.75rem;
        border: none;
        border-radius: 8px;
        font-size: 1rem;
        font-weight: 600;
        cursor: pointer;
        transition: background 0.15s;
        background: var(--attestto-primary, #594fd3);
        color: white;
      }

      .sign-btn:hover {
        background: var(--attestto-primary-hover, #7b72ed);
      }

      .sign-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .connect-btn {
        width: 100%;
        padding: 0.65rem;
        border: 1px solid var(--attestto-primary, #594fd3);
        border-radius: 8px;
        font-size: 0.9rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.15s;
        background: transparent;
        color: var(--attestto-primary, #594fd3);
        margin-bottom: 1rem;
      }

      .connect-btn:hover {
        background: var(--attestto-primary, #594fd3);
        color: white;
      }

      .step-indicator {
        display: flex;
        gap: 1rem;
        margin-bottom: 1.5rem;
        font-size: 0.8rem;
        color: var(--attestto-text-muted, #64748b);
      }

      .step {
        display: flex;
        align-items: center;
        gap: 0.35rem;
      }

      .step-active {
        color: var(--attestto-primary, #594fd3);
        font-weight: 600;
      }

      .step-done {
        color: var(--attestto-success, #16a34a);
      }

      /* ── Download Modal ──────────────────────────────────── */
      .modal-overlay {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.7);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
      }

      .modal-card {
        background: var(--attestto-bg-card, #0f172a);
        border: 1px solid var(--attestto-border, #334155);
        border-radius: 16px;
        padding: 2rem;
        max-width: 420px;
        width: 90%;
        text-align: center;
      }

      .modal-icon { font-size: 2.5rem; margin-bottom: 1rem; }

      .modal-title {
        font-size: 1.1rem;
        font-weight: 700;
        margin-bottom: 0.5rem;
      }

      .modal-filename {
        font-family: 'SF Mono', 'Fira Code', monospace;
        font-size: 0.82rem;
        color: var(--attestto-text-muted, #94a3b8);
        word-break: break-all;
        margin-bottom: 1.5rem;
        padding: 0.5rem;
        background: var(--attestto-bg-code, #1e293b);
        border-radius: 6px;
      }

      .modal-verify-btn {
        width: 100%;
        padding: 0.75rem;
        border: none;
        border-radius: 8px;
        font-size: 1rem;
        font-weight: 600;
        cursor: pointer;
        background: var(--attestto-primary, #594fd3);
        color: white;
        transition: background 0.15s;
        margin-bottom: 0.5rem;
      }

      .modal-verify-btn:hover {
        background: var(--attestto-primary-hover, #7b72ed);
      }

      .modal-dismiss {
        background: none;
        border: none;
        color: var(--attestto-text-muted, #94a3b8);
        cursor: pointer;
        font-size: 0.82rem;
        padding: 0.5rem;
      }

      .modal-dismiss:hover { color: var(--attestto-text, #e2e8f0); }

      .download-link {
        display: block;
        text-align: center;
        margin-top: 1rem;
        padding: 0.75rem;
        background: var(--attestto-success-bg, #dcfce7);
        color: var(--attestto-success, #16a34a);
        border-radius: 8px;
        text-decoration: none;
        font-weight: 600;
      }

      /* ── Signing Loading State ──────────────────────── */
      .signing-status {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 1.5rem;
        text-align: center;
      }

      .signing-beans {
        display: flex;
        gap: 0.5rem;
        margin-bottom: 1rem;
      }

      .signing-bean {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: var(--attestto-primary, #594fd3);
        animation: signing-bounce 1.4s ease-in-out infinite;
      }

      .signing-bean:nth-child(2) { animation-delay: 0.16s; }
      .signing-bean:nth-child(3) { animation-delay: 0.32s; }

      @keyframes signing-bounce {
        0%, 80%, 100% {
          transform: scale(0.6);
          opacity: 0.4;
        }
        40% {
          transform: scale(1);
          opacity: 1;
        }
      }

      .signing-step {
        font-size: 0.9rem;
        font-weight: 500;
        margin-bottom: 0.35rem;
      }

      .signing-hint {
        font-size: 0.72rem;
        color: var(--attestto-text-muted, #64748b);
      }
    `,
  ]

  @state() private dragging = false
  @state() private file: File | null = null
  @state() private discovering = false
  @state() private wallets: WalletAnnouncement[] = []
  @state() private selectedWallet: WalletAnnouncement | null = null
  @state() private useBrowserKey = false
  @state() private signing = false
  @state() private signingStatus = ''
  @state() private signed = false
  @state() private signedCredential: DocumentSignatureCredential | null = null
  @state() private error: string | null = null
  @state() private pdfMeta: PdfMeta | null = null
  @state() private downloaded = false
  @state() private downloadFileName = ''
  @state() private showDownloadModal = false
  @state() private _lang: Lang = currentLang()

  private _onLangChange = (e: Event) => {
    this._lang = (e as CustomEvent).detail.lang
  }

  override connectedCallback() {
    super.connectedCallback()
    this.discover()
    window.addEventListener('attestto-lang-change', this._onLangChange)
  }

  override disconnectedCallback() {
    super.disconnectedCallback()
    window.removeEventListener('attestto-lang-change', this._onLangChange)
  }

  override render() {
    return html`
      ${this.renderWalletStatus()} ${this.file ? this.renderSignFlow() : this.renderDropZone()}
      ${this.showDownloadModal ? this.renderDownloadModal() : ''}
    `
  }

  // ── Wallet Status ──────────────────────────────────────────────────

  private renderWalletStatus() {
    if (this.discovering) {
      return html`
        <div class="wallet-status wallet-discovering">${t('comp.sign.discovering')}</div>
      `
    }

    if (this.selectedWallet) {
      return html`
        <div class="wallet-card" part="wallet-card">
          <img
            class="wallet-card-icon"
            src=${this.selectedWallet.icon}
            alt=${this.selectedWallet.name}
            @error=${(e: Event) => {
              ;(e.target as HTMLImageElement).style.display = 'none'
            }}
          />
          <div class="wallet-card-info">
            <div class="wallet-card-name">${this.selectedWallet.name}</div>
            <div class="wallet-card-meta">${t('comp.sign.by')} ${this.selectedWallet.maintainer.name}</div>
          </div>
          <button class="wallet-card-disconnect" @click=${this.disconnect}>${t('comp.sign.change')}</button>
        </div>
      `
    }

    if (this.wallets.length > 0) {
      return html`
        <button class="connect-btn" @click=${this.pickFromDiscovered}>
          ${this.wallets.length} ${t('comp.sign.walletsFound')}
        </button>
      `
    }

    if (this.useBrowserKey) {
      return html`
        <div class="wallet-card" part="wallet-card">
          <div
            style="width: 32px; height: 32px; border-radius: 6px; background: var(--attestto-primary, #594FD3); display: flex; align-items: center; justify-content: center; color: white; font-size: 0.9rem; font-weight: 700; flex-shrink: 0;"
          >
            &#x1f511;
          </div>
          <div class="wallet-card-info">
            <div class="wallet-card-name">${t('comp.sign.browserKey')}</div>
            <div class="wallet-card-meta">${t('comp.sign.selfIssued')} &middot; did:key</div>
          </div>
          <button class="wallet-card-disconnect" @click=${this.disconnect}>${t('comp.sign.change')}</button>
        </div>
      `
    }

    return html`
      <div class="wallet-status wallet-missing" style="flex-wrap: wrap; gap: 0.5rem;">
        <span>${t('comp.sign.noWallet')}</span>
        <span style="display: flex; gap: 0.5rem; margin-left: auto;">
          <button
            style="background: none; border: 1px solid currentColor; padding: 0.25rem 0.6rem; border-radius: 4px; color: inherit; cursor: pointer; font-size: 0.75rem;"
            @click=${this.enableBrowserKey}
          >
            ${t('comp.sign.signBrowserKey')}
          </button>
          <button
            style="background: none; border: 1px solid currentColor; padding: 0.25rem 0.6rem; border-radius: 4px; color: inherit; cursor: pointer; font-size: 0.75rem;"
            @click=${this.discover}
          >
            ${t('comp.sign.retry')}
          </button>
        </span>
      </div>
    `
  }

  private renderDropZone() {
    return html`
      <div
        class="drop-zone ${this.dragging ? 'dragging' : ''}"
        @click=${this.openFilePicker}
        @dragover=${this.onDragOver}
        @dragleave=${this.onDragLeave}
        @drop=${this.onDrop}
      >
        <div class="drop-zone-icon">${this.selectedWallet || this.useBrowserKey ? '✍️' : '📄'}</div>
        <div style="font-size: 1rem; color: var(--attestto-text-muted, #64748b)">
          ${this.selectedWallet
            ? t('comp.sign.dropDid')
            : this.useBrowserKey
              ? t('comp.sign.dropBrowserKey')
              : t('comp.sign.dropSign')}
        </div>
        <div
          style="font-size: 0.8rem; color: var(--attestto-text-muted, #94a3b8); margin-top: 0.5rem"
        >
          ${t('comp.sign.fileNeverLeaves')}
        </div>
        <input type="file" @change=${this.onFileSelect} accept=".pdf" />
      </div>
    `
  }

  private renderSignFlow() {
    return html`
      <div class="sign-card">
        <div class="step-indicator">
          <span class="step ${this.file ? 'step-done' : 'step-active'}">${t('comp.sign.selectPdf')}</span>
          <span
            class="step ${this.signed
              ? 'step-done'
              : this.file && !this.signed
                ? 'step-active'
                : ''}"
            >${t('comp.sign.signStep')}</span
          >
          <span class="step ${this.signed ? 'step-done' : ''}">${t('comp.sign.downloadStep')}</span>
        </div>

        <div class="file-info">
          📄 ${this.file!.name}
          <span
            style="font-weight: 400; font-size: 0.8rem; color: var(--attestto-text-muted, #64748b)"
          >
            ${this.formatSize(this.file!.size)}
          </span>
        </div>

        ${this.pdfMeta ? html`
          <div style="display: grid; grid-template-columns: auto 1fr; gap: 0.25rem 0.75rem; font-size: 0.82rem; margin-bottom: 1rem; padding: 0.75rem; background: var(--attestto-bg-code, #f1f5f9); border-radius: 8px;">
            ${this.pdfMeta.title ? html`<span style="color: var(--attestto-text-muted, #64748b); font-weight: 500;">${t('comp.verify.title')}</span><span>${this.pdfMeta.title}</span>` : ''}
            ${this.pdfMeta.author ? html`<span style="color: var(--attestto-text-muted, #64748b); font-weight: 500;">${t('comp.verify.author')}</span><span>${this.pdfMeta.author}</span>` : ''}
            ${this.pdfMeta.pages ? html`<span style="color: var(--attestto-text-muted, #64748b); font-weight: 500;">${t('audit.pages')}</span><span>${this.pdfMeta.pages}</span>` : ''}
            ${this.pdfMeta.creator ? html`<span style="color: var(--attestto-text-muted, #64748b); font-weight: 500;">${t('comp.verify.creator')}</span><span>${this.pdfMeta.creator}</span>` : ''}
            ${this.pdfMeta.creationDate ? html`<span style="color: var(--attestto-text-muted, #64748b); font-weight: 500;">${t('comp.verify.created')}</span><span>${this.pdfMeta.creationDate}</span>` : ''}
            ${this.pdfMeta.modDate ? html`<span style="color: var(--attestto-text-muted, #64748b); font-weight: 500;">${t('comp.verify.modified')}</span><span>${this.pdfMeta.modDate}</span>` : ''}
          </div>
        ` : ''}

        ${this.error
          ? html`<div
              style="color: var(--attestto-warning, #d97706); font-size: 0.85rem; margin-bottom: 1rem"
            >
              ${this.error}
              ${this.selectedWallet && !this.useBrowserKey
                ? html`<br /><button
                    style="background: none; border: none; color: var(--attestto-primary, #594fd3); cursor: pointer; font-size: 0.82rem; text-decoration: underline; padding: 0.25rem 0 0;"
                    @click=${this.enableBrowserKey}
                  >${t('comp.sign.useBrowserKeyInstead')}</button>`
                : ''}
            </div>`
          : ''}
        ${!this.signed
          ? this.signing
            ? html`
                <div class="signing-status">
                  <div class="signing-beans">
                    <span class="signing-bean"></span>
                    <span class="signing-bean"></span>
                    <span class="signing-bean"></span>
                  </div>
                  <div class="signing-step">${this.signingStatus || t('comp.sign.signing')}</div>
                  <div class="signing-hint">${t('comp.sign.signingHint')}</div>
                </div>
              `
            : html`
                <button
                  class="sign-btn"
                  ?disabled=${!this.selectedWallet && !this.useBrowserKey}
                  @click=${this.sign}
                >
                  ${this.selectedWallet
                    ? `${t('comp.sign.signWith')} ${this.selectedWallet.name}`
                    : this.useBrowserKey
                      ? t('comp.sign.signWithBrowserKey')
                      : t('comp.sign.connectFirst')}
                </button>
              `
          : html`
              <div class="download-link" style="cursor: default; margin-bottom: 0.75rem;">
                ${this.useBrowserKey
                  ? t('comp.sign.signedBrowserKey')
                  : t('comp.sign.signedWallet')}
              </div>
              ${this.downloaded
                ? html`
                    <button
                      class="sign-btn"
                      @click=${() => { window.location.href = '/' }}
                    >
                      ${t('comp.sign.verifyDoc')}
                    </button>
                  `
                : html`
                    <button
                      class="sign-btn"
                      style="background: #e2e8f0; color: #0f172a;"
                      @click=${this.downloadSignedPdf}
                    >
                      ${t('comp.sign.downloadPdf')}
                    </button>
                  `}
              <button
                style="display: block; width: 100%; margin-top: 0.5rem; padding: 0.5rem; background: none; border: 1px solid var(--attestto-border, #334155); border-radius: 8px; color: var(--attestto-text-muted, #94a3b8); cursor: pointer; font-size: 0.82rem;"
                @click=${this.handleExport}
              >
                ${t('comp.sign.exportJson')}
              </button>
            `}

        <div style="text-align: center; margin-top: 0.75rem">
          <button
            style="background: none; border: none; color: var(--attestto-text-muted, #94a3b8); cursor: pointer; font-size: 0.8rem"
            @click=${this.reset}
          >
            ${this.signed ? t('comp.sign.signAnother') : t('comp.sign.cancelBtn')}
          </button>
        </div>
      </div>
    `
  }

  // ── Wallet Discovery ──────────────────────────────────────────────

  private async discover() {
    this.discovering = true
    try {
      this.wallets = await discoverWallets(1500)
      if (this.wallets.length === 1) {
        this.selectedWallet = this.wallets[0]
      }
    } finally {
      this.discovering = false
    }
  }

  private async pickFromDiscovered() {
    const wallet = await pickWallet({ timeoutMs: 2000 })
    if (wallet) {
      this.selectedWallet = wallet
    }
  }

  private disconnect() {
    this.selectedWallet = null
    this.useBrowserKey = false
  }

  private async enableBrowserKey() {
    await getBrowserKeyPair()
    this.useBrowserKey = true
  }

  // ── File Handlers ─────────────────────────────────────────────────

  private onDragOver(e: DragEvent) {
    e.preventDefault()
    this.dragging = true
  }

  private onDragLeave() {
    this.dragging = false
  }

  private onDrop(e: DragEvent) {
    e.preventDefault()
    this.dragging = false
    const file = e.dataTransfer?.files[0]
    if (file && file.name.toLowerCase().endsWith('.pdf')) {
      this.file = file
      this.error = null
      this.extractMetadata(file)
    } else {
      this.error = t('comp.sign.onlyPdf')
    }
  }

  private openFilePicker() {
    const input = this.shadowRoot?.querySelector('input[type="file"]') as HTMLInputElement
    input?.click()
  }

  private onFileSelect(e: Event) {
    const input = e.target as HTMLInputElement
    const file = input.files?.[0]
    if (file) {
      this.file = file
      this.error = null
      this.extractMetadata(file)
    }
  }

  private async extractMetadata(file: File): Promise<void> {
    this.pdfMeta = null
    try {
      const pdfjsLib = await loadPdfJs()
      if (!pdfjsLib) return
      const buffer = await file.arrayBuffer()
      const pdf = await pdfjsLib.getDocument({ data: buffer }).promise
      const meta = await pdf.getMetadata()
      const info = meta?.info as Record<string, unknown> | undefined
      this.pdfMeta = {
        title: (info?.Title as string) || null,
        author: (info?.Author as string) || null,
        subject: (info?.Subject as string) || null,
        creator: (info?.Creator as string) || null,
        producer: (info?.Producer as string) || null,
        creationDate: info?.CreationDate ? formatPdfDate(info.CreationDate as string) : null,
        modDate: info?.ModDate ? formatPdfDate(info.ModDate as string) : null,
        pages: pdf.numPages ?? null,
      }
    } catch {
      // Metadata extraction is best-effort
    }
  }

  // ── Signing (delegates to composable) ─────────────────────────────

  private async sign() {
    if (!this.file) return
    if (!this.selectedWallet && !this.useBrowserKey) return

    this.signing = true
    this.signingStatus = t('comp.sign.computingHash')
    this.error = null

    try {
      const hash = await hashFile(this.file)

      if (this.selectedWallet) {
        this.signingStatus = t('comp.sign.waitingWallet')
        const result = await signWithWallet(this.selectedWallet, this.file, hash)
        if (!result) {
          this.error = t('comp.sign.walletTimeout')
          return
        }
        this.signedCredential = result.credential
      } else {
        this.signingStatus = t('comp.sign.signingBrowserKey')
        const result = await signWithBrowserKey(this.file, hash)
        this.signedCredential = result.credential
      }

      this.signed = true
    } catch (err) {
      this.error = err instanceof Error ? err.message : t('comp.sign.signingFailed')
    } finally {
      this.signing = false
      this.signingStatus = ''
    }
  }

  private async downloadSignedPdf() {
    if (!this.file) return

    const date = new Date().toISOString().slice(0, 10)
    const baseName = this.file.name.replace(/\.pdf$/i, '')
    const fileName = `${baseName}-signed-${date}.pdf`

    this.downloadFileName = fileName
    this.showDownloadModal = true

    try {
      // Use the canonical Attestto self-attested signer so the resulting
      // PDF is recognized by verify.attestto.com (post-ATT-361). The old
      // injectAttestationPage path produced a pdf-lib full-rewrite with
      // no embedded keyword payload — verifier reported UNSIGNED.
      //
      // Two paths:
      //   - selectedWallet → route through the extension's Ed25519 vault
      //     key via the new ATTESTTO_SIGN_PDF_REQUEST flow (ATT-364)
      //   - no wallet → ephemeral browser keypair, single-use
      const externalSigner = this.selectedWallet
        ? buildExtensionSigner({
            fileName: this.file.name,
            documentHash: '', // signer composable computes the hash
            did: this.selectedWallet.did,
          })
        : undefined

      const { pdfBytes } = await signPdfSelfAttested(this.file, {
        signerName: this.selectedWallet?.name || 'Browser-ephemeral key',
        externalSigner,
      })

      const blob = new Blob([pdfBytes as unknown as BlobPart], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Failed to sign PDF'
      // Fall back to the original unsigned file so the user at least
      // gets their document back.
      const blob = new Blob([this.file], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      a.click()
      URL.revokeObjectURL(url)
    }

    this.downloaded = true
  }

  private renderDownloadModal() {
    return html`
      <div class="modal-overlay" @click=${(e: Event) => { if (e.target === e.currentTarget) this.showDownloadModal = false }}>
        <div class="modal-card">
          <div class="modal-icon">✅</div>
          <div class="modal-title">${t('comp.sign.modalTitle')}</div>
          <div class="modal-filename">${this.downloadFileName}</div>
          <button class="modal-verify-btn" @click=${() => { window.location.href = '/' }}>
            ${t('comp.sign.modalVerify')}
          </button>
          <button class="modal-dismiss" @click=${() => { this.showDownloadModal = false }}>
            ${t('comp.sign.modalContinue')}
          </button>
        </div>
      </div>
    `
  }

  private handleExport() {
    if (!this.signedCredential) return
    exportCredentialAsJson(this.signedCredential, this.file?.name)
  }

  private reset() {
    this.file = null
    this.signed = false
    this.signing = false
    this.signedCredential = null
    this.error = null
    this.pdfMeta = null
    this.downloaded = false
    this.downloadFileName = ''
    this.showDownloadModal = false
  }

  private formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'attestto-sign': AttesttoSign
  }
}
